/**
 * Reset Password Page Logic
 *
 * Handles password reset with token from email
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { injectLayout } from '../app/layout.js';
import { applyTheme } from '../app/theme.js';
import { formSubmitHandler } from '../ui/forms.js';
import { adSyncWarning } from '../api/ad-sync.js';

/**
 * Get the token from the URL
 */
function getToken(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('token') || '';
}

/**
 * Handle password reset form submission
 */
async function handleResetPassword(): Promise<void> {
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const confirmInput = document.getElementById('confirm') as HTMLInputElement;
  const errorDiv = document.getElementById('reset-error');
  if (errorDiv) errorDiv.textContent = '';

  const password = passwordInput?.value || '';
  const confirm = confirmInput?.value || '';

  if (password !== confirm) {
    if (errorDiv) errorDiv.textContent = 'Passwords do not match';
    return;
  }

  if (password.length < 8) {
    if (errorDiv) errorDiv.textContent = 'Password must be at least 8 characters long';
    return;
  }

  const token = getToken();

  const result = await apiCall(api.resetPassword(token, password), undefined, (err) => {
    if (errorDiv) errorDiv.textContent = err.message;
  });
  if (result === null) return;

  const adWarning = document.getElementById('reset-ad-warning');
  const warning = adSyncWarning(result);
  if (adWarning && warning) {
    adWarning.textContent = warning;
    adWarning.style.display = 'block';
  }

  // Show success view
  const formView = document.getElementById('reset-form-view');
  const successView = document.getElementById('reset-success-view');
  if (formView) formView.style.display = 'none';
  if (successView) successView.style.display = 'block';
}

/**
 * Initialize the reset password page
 */
export async function initResetPasswordPage(): Promise<void> {
  applyTheme();
  // Inject minimal layout
  injectLayout(null);

  const token = getToken();
  const errorView = document.getElementById('reset-error-view');
  const formView = document.getElementById('reset-form-view');

  if (!token) {
    // No token provided
    if (errorView) {
      errorView.style.display = 'block';
      const errorMsg = document.getElementById('reset-error-message');
      if (errorMsg) errorMsg.textContent = 'No reset token provided.';
    }
    return;
  }

  // Show the form (token validation happens on submit)
  if (formView) formView.style.display = 'block';

  // Set up form handler with loading state
  const form = document.getElementById('reset-password-form');
  if (form) {
    formSubmitHandler(form, handleResetPassword);
  }
}

// Export to window
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).initResetPasswordPage = initResetPasswordPage;
}
